import { isLoadViewSuggestionUnsupportedForTms } from 'components/Kit/EmailSuggestions/utils/loadViewSuggestionTmsSupport';
import { Maybe } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';

export type CompleteKitSuggestionResult = {
  curSuggestionList: GenericSuggestion[];
  clickedSuggestion: Maybe<GenericSuggestion>;
  /** True when any non-Accepted suggestion remains in Kit (any pipeline). */
  hasRemainingPending: boolean;
  /** True when every suggestion in the completed pipeline is Accepted. */
  pipelineFullyCompleted: boolean;
};

type CompleteKitSidebarPatch = {
  curSuggestionList: GenericSuggestion[];
  clickedSuggestion: Maybe<GenericSuggestion>;
  batchSelectedSuggestionIds: number[];
  /** Arm the Kit "Scroll to top" pill when pending suggestions remain. */
  showKitScrollToTop: boolean;
};

/**
 * Marks one or more Kit suggestions as Accepted.
 * Skips TMS-unsupported Carrier Info / Appointment pills when `tmsName` is set.
 * Special-transport Quick Quotes remain eligible for auto-advance.
 */
export function completeKitSuggestions(
  curSuggestionList: GenericSuggestion[],
  completedIds: number | number[],
  pipeline: SuggestionPipelines,
  _tmsName?: Maybe<string>
): CompleteKitSuggestionResult {
  const completedIdList = Array.isArray(completedIds)
    ? completedIds
    : [completedIds];
  const completedIdSet = new Set(completedIdList);

  const nextList = curSuggestionList.map((s) =>
    completedIdSet.has(s.id) ? { ...s, status: SuggestionStatus.Accepted } : s
  );

  // Keep the just-completed suggestion selected (last id for batch completes).
  const primaryCompletedId = completedIdList[completedIdList.length - 1];
  const clickedSuggestion =
    nextList.find((s) => s.id === primaryCompletedId) ?? null;

  const pipelineSuggestions = nextList.filter((s) => s.pipeline === pipeline);
  const pipelineFullyCompleted =
    pipelineSuggestions.length > 0 &&
    pipelineSuggestions.every((s) => s.status === SuggestionStatus.Accepted);

  const hasRemainingPending = nextList.some(
    (s) => s.status !== SuggestionStatus.Accepted
  );

  return {
    curSuggestionList: nextList,
    clickedSuggestion,
    hasRemainingPending,
    pipelineFullyCompleted,
  };
}

/**
 * Sidebar state patch after a terminating action: mark Accepted and
 * arm the scroll-to-top pill when pending suggestions remain.
 */
export function applyCompleteKitSuggestions(
  prevState: {
    curSuggestionList: GenericSuggestion[];
    batchSelectedSuggestionIds?: number[];
  },
  completedIds: number | number[],
  pipeline: SuggestionPipelines,
  tmsName?: Maybe<string>
): CompleteKitSidebarPatch {
  const update = completeKitSuggestions(
    prevState.curSuggestionList,
    completedIds,
    pipeline,
    tmsName
  );
  const completedIdSet = new Set(
    Array.isArray(completedIds) ? completedIds : [completedIds]
  );

  return {
    curSuggestionList: update.curSuggestionList,
    clickedSuggestion: update.clickedSuggestion,
    batchSelectedSuggestionIds: (
      prevState.batchSelectedSuggestionIds ?? []
    ).filter((id) => !completedIdSet.has(id)),
    showKitScrollToTop: update.hasRemainingPending,
  };
}

/**
 * First non-Accepted suggestion in the given pipeline (list order).
 * When `tmsName` is provided, skips Carrier Info / Appointment pills the TMS
 * cannot apply. Does not skip Special-transport Quick Quotes.
 */
export function findNextPendingInPipeline(
  curSuggestionList: GenericSuggestion[],
  pipeline: SuggestionPipelines,
  tmsName?: Maybe<string>
): Maybe<GenericSuggestion> {
  return (
    curSuggestionList.find(
      (s) =>
        s.pipeline === pipeline &&
        s.status !== SuggestionStatus.Accepted &&
        !isLoadViewSuggestionUnsupportedForTms(s, tmsName ?? null)
    ) ?? null
  );
}

/**
 * Resolve which suggestion to select when switching to the Quick Quote or
 * Load Building tab. Keeps the current selection if it is already in that
 * pipeline; otherwise picks the first pending suggestion in the pipeline.
 *
 * Returns null when the destination pipeline has nothing pending.
 */
export function resolveClickedSuggestionForPipelineTab(
  curSuggestionList: GenericSuggestion[],
  clickedSuggestion: Maybe<GenericSuggestion>,
  pipeline: SuggestionPipelines
): Maybe<GenericSuggestion> {
  if (clickedSuggestion?.pipeline === pipeline) {
    return clickedSuggestion;
  }

  return findNextPendingInPipeline(curSuggestionList, pipeline);
}

// Whether a suggestion is still actionable in Kit; excludes Accepted and Rejected
export function isKitSuggestionActionable(suggestion: {
  status: SuggestionStatus;
}): boolean {
  return (
    suggestion.status === SuggestionStatus.Pending ||
    suggestion.status === SuggestionStatus.InFlight
  );
}
